import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    FormField,
    Input,
    Container,
    Header,
    SpaceBetween,
    Select,
    Alert,
    Checkbox,
} from '@amzn/awsui-components-react-v3';
import {
    ActivityModality,
    ActivityProgram,
    ActivityStatus,
    AudienceType,
    BasicActivityFormSectionProps,
} from '../../Common/Common';
import {
    getActivityStatusesList,
    resetActivityStatusesSlice,
    selectAllActiveActivityStatuses,
    selectError as selectActivityStatusListError,
    selectIsLoaded as selectIsActivityStatusListLoaded,
    selectIsLoading as selectIsActivityStatusListLoading,
} from '../../../../../common/store/slices/activityStatusesSlice';
import {
    getActivityAudiencesList,
    resetActivityAudiencesSlice,
    selectIsLoaded as selectIsActivityAudienceListLoaded,
    selectIsLoading as selectIsActivityAudienceListLoading,
} from '../../../../../common/store/slices/activityAudiencesSlice';
import {
    getActivityModalitiesList,
    resetActivityModalitiesSlice,
    selectAllActiveActivityModalities,
    selectError as selectActivityModalityListError,
    selectIsLoaded as selectIsActivityModalityListLoaded,
    selectIsLoading as selectIsActivityModalityListLoading,
} from '../../../../../common/store/slices/activityModalitiesSlice';
import {
    FORM_ERROR_SELECTOR,
    getOptionsAndLookupForSelectInputPolarisV3,
    getStatusType,
} from '../../../../../imt/components/Instructor/FormSections/FormSections.common';
import { ActivityStatusItemData } from '../../../../../common/interfaces/businessDataItem/activityStatusItem';
import { ActivityModalityItemData } from '../../../../../common/interfaces/businessDataItem/activityModalityItem';
import DeliveryAddressSection from './Sections/DeliveryAddressSection';
import StudentCountSection from './Sections/StudentCountSection';
import { computeAdditionalFieldsVisibility } from '../../Edit/EditBaseDetailsForm';
import PartnerInitiativeSection from './Sections/PartnerInitiativeSection';
import SelectLMSType from '../CommonFormFields/SelectLMSType';
import ClassSizeInput from '../CommonFormFields/ClassSizeInput';
import SelectVILTType from '../CommonFormFields/SelectVILTType';
import AudienceListSelect from '../CommonFormFields/AudienceListSelect';
import OrganizationGoalsSelect, {
    hasOrganizationId,
} from './Sections/OrganizationGoalsSelect';
import { checkFeature } from '../../../../../common/utils/featureFlag';
import { selectFeatures } from '../../../../../common/store/slices/featureSlice';

export type EditBaseDetailsFormSectionProps = BasicActivityFormSectionProps & {
    isManualMarkAttendanceNeeded: boolean;
};

const EditBaseDetailsFormSection = ({
    formValues,
    errors,
    handleFieldEvent,
    setShowAdditionalFields,
    showAdditionalFields,
    isManualMarkAttendanceNeeded,
    initialFormState,
}: EditBaseDetailsFormSectionProps) => {
    const isActivityStatusListLoading = useSelector(
        selectIsActivityStatusListLoading,
    );
    const isActivityStatusListLoaded = useSelector(
        selectIsActivityStatusListLoaded,
    );
    const activityStatusListError = useSelector(selectActivityStatusListError);
    const featureFlags = useSelector(selectFeatures);
    const isLearningGoalsEnabled = !!checkFeature(
        '',
        { featureName: 'learning-goals' },
        featureFlags?.features,
    );
    const isActivityAudienceListLoading = useSelector(
        selectIsActivityAudienceListLoading,
    );
    const isActivityAudienceListLoaded = useSelector(
        selectIsActivityAudienceListLoaded,
    );

    const isActivityModalityListLoading = useSelector(
        selectIsActivityModalityListLoading,
    );
    const isActivityModalityListLoaded = useSelector(
        selectIsActivityModalityListLoaded,
    );
    const activityModalityListError = useSelector(
        selectActivityModalityListError,
    );
    const activityStatusList = useSelector(selectAllActiveActivityStatuses);
    const activityModalityList = useSelector(selectAllActiveActivityModalities);

    const [isPartnerInitiativeVisible, setIsPartnerInitiativeVisible] =
        useState(false);

    const [showInstructorWarning, setShowInstructorWarning] = useState(false);

    useEffect(() => {
        // Check if status is being set to Active and there are no instructors
        if (
            formValues.activity_status === ActivityStatus.Active &&
            (!initialFormState.instructors ||
                initialFormState.instructors.length === 0)
        ) {
            setShowInstructorWarning(true);
        } else {
            setShowInstructorWarning(false);
        }
    }, [formValues.activity_status, initialFormState.instructors]);

    useEffect(() => {
        const isPartnerProgram = formValues.program === ActivityProgram.Partner;
        setIsPartnerInitiativeVisible(isPartnerProgram);
    }, [formValues]);

    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            // reset business data slices
            // this code block should only run once
            [
                resetActivityStatusesSlice,
                resetActivityAudiencesSlice,
                resetActivityModalitiesSlice,
            ].forEach((resetFunction) => dispatch(resetFunction()));
        };
    }, [dispatch]);

    // lifecycle method to fetch (and re- fetch) business data
    useEffect(() => {
        (
            [
                [
                    !isActivityStatusListLoaded && !isActivityStatusListLoading,
                    getActivityStatusesList,
                ],
                [
                    !isActivityAudienceListLoaded &&
                        !isActivityAudienceListLoading,
                    getActivityAudiencesList,
                ],
                [
                    !isActivityModalityListLoaded &&
                        !isActivityModalityListLoading,
                    getActivityModalitiesList,
                ],
            ] as ReadonlyArray<[boolean, Function]>
        ).forEach(([shouldFetch, getList]) => {
            if (shouldFetch) {
                dispatch(getList());
            }
        });
    });

    // https://quip-amazon.com/fLHPAp8B8bUU/Activity-Status-Validations
    useEffect(() => {
        setShowAdditionalFields(
            computeAdditionalFieldsVisibility(
                formValues,
                initialFormState,
                isManualMarkAttendanceNeeded,
            ),
        );
    }, [formValues]);

    const {
        valueLookup: activityStatusLookup,
        valueOptions: activityStatusOptions,
    } = getOptionsAndLookupForSelectInputPolarisV3<ActivityStatusItemData>(
        activityStatusList,
        (activityStatus: ActivityStatusItemData) => ({
            label: activityStatus.activity_status,
            value: activityStatus.pk as string,
        }),
    );

    const {
        valueLookup: activityModalityLookup,
        valueOptions: activityModalityOptions,
    } = getOptionsAndLookupForSelectInputPolarisV3<ActivityModalityItemData>(
        activityModalityList,
        (activityModality: ActivityModalityItemData) => ({
            label: activityModality.activity_modality,
            value: activityModality.pk as string,
        }),
    );

    return (
        <>
            <Container
                data-testid="EditBaseDetailsFormSection"
                header={<Header variant="h2">Activity details</Header>}
            >
                <SpaceBetween direction="vertical" size="l">
                    <FormField
                        label="Activity name"
                        errorText={errors?.activity_name}
                        data-testid="formField-EditActivityName"
                    >
                        <Input
                            value={formValues.activity_name}
                            placeholder="Enter an activity name"
                            onChange={(e) =>
                                handleFieldEvent({
                                    activity_name: e.detail.value,
                                })
                            }
                            data-testid={`EditActivityName`}
                        />
                    </FormField>
                    {!!checkFeature(
                        '',
                        { featureName: 'activity-name-visibility' },
                        featureFlags?.features,
                    ) && (
                        <FormField
                            label="Activity name visibility"
                            description="Controls whether this activity name is displayed to customers in myClass. When unchecked, customers see the course name instead."
                        >
                            <Checkbox
                                onChange={({ detail }) =>
                                    handleFieldEvent({
                                        activity_name_visibility: detail.checked
                                            ? 'PUBLIC'
                                            : 'INTERNAL',
                                    })
                                }
                                checked={
                                    formValues.activity_name_visibility ===
                                    'PUBLIC'
                                }
                                data-testid="activity_name_visibility_toggle"
                            >
                                Approved for customer display
                            </Checkbox>
                        </FormField>
                    )}
                    <FormField label="Activity types">
                        {formValues.activity_type}
                    </FormField>
                    <FormField label="Program">{formValues.program}</FormField>

                    <FormField label="Provider">
                        {formValues.provider}
                    </FormField>
                    <FormField
                        errorText={errors?.activity_status}
                        label="Activity status"
                    >
                        <Select
                            className={
                                errors?.activity_status && FORM_ERROR_SELECTOR
                            }
                            placeholder={
                                isActivityStatusListLoading
                                    ? 'Loading activity statuses'
                                    : 'Select an activity status'
                            }
                            options={activityStatusOptions}
                            selectedOption={
                                activityStatusLookup[formValues.activity_status]
                            }
                            onChange={(e) => {
                                handleFieldEvent({
                                    activity_status:
                                        e.detail.selectedOption.label,
                                });
                            }}
                            loadingText="Loading activity statuses"
                            errorText="An error occurred while loading activity statuses"
                            empty="No activity statuses found"
                            statusType={getStatusType(
                                isActivityStatusListLoading,
                                isActivityStatusListLoaded,
                                activityStatusListError,
                            )}
                            disabled={!isActivityStatusListLoaded}
                            data-testid={`EditBaseDetailsActivityStatus`}
                        />
                    </FormField>

                    {showInstructorWarning && (
                        <Alert
                            type="warning"
                            header="Instructor Required"
                            data-testid="instructor-required-alert"
                        >
                            At least one instructor must be assigned to create a
                            classroom for this activity. Please assign an
                            instructor after saving this form.
                        </Alert>
                    )}

                    {showAdditionalFields?.showDeliveryAddress && (
                        <DeliveryAddressSection
                            {...{
                                handleFieldEvent,
                                formValues,
                                errors,
                            }}
                        />
                    )}

                    {showAdditionalFields?.showNumberOfStudentsAttended && (
                        <StudentCountSection
                            {...{
                                handleFieldEvent,
                                formValues,
                                errors,
                            }}
                        />
                    )}

                    {showAdditionalFields?.showLMSType && (
                        <SelectLMSType
                            {...{
                                handleFieldEvent,
                                formValues: {
                                    activity_modality:
                                        formValues.activity_modality,
                                    activity_type: formValues.activity_type,
                                    lms_type: formValues.lms_type,
                                    program: formValues.program,
                                    v_ilt_type: formValues?.v_ilt_type,
                                },
                                errors: {
                                    activity_modality: errors.activity_modality,
                                    lms_type: errors.lms_type,
                                    v_ilt_type: errors.v_ilt_type,
                                },
                            }}
                        />
                    )}

                    {showAdditionalFields?.showVILTType && (
                        <SelectVILTType
                            {...{
                                handleFieldEvent,
                                formValues: {
                                    lms_type: formValues.lms_type,
                                    v_ilt_type: formValues?.v_ilt_type,
                                },
                                errors: {
                                    lms_type: errors.lms_type,
                                    v_ilt_type: errors.v_ilt_type,
                                },
                            }}
                        />
                    )}

                    {showAdditionalFields?.showClassSize && (
                        <ClassSizeInput
                            {...{
                                setClassSize: handleFieldEvent,
                                errors,
                                classSize: formValues.class_size,
                            }}
                        />
                    )}

                    {isPartnerInitiativeVisible && (
                        <PartnerInitiativeSection
                            {...{
                                handleFieldEvent,
                                formValues,
                                errors,
                            }}
                        />
                    )}

                    <AudienceListSelect
                        {...{
                            handleFieldEvent,
                            formValues: {
                                activity_audience: formValues.activity_audience,
                                activity_modality: formValues.activity_modality,
                                program: formValues.program,
                            },
                            errors,
                        }}
                    />

                    <FormField
                        errorText={errors?.activity_modality}
                        label="Modality"
                    >
                        <Select
                            className={
                                errors?.activity_modality && FORM_ERROR_SELECTOR
                            }
                            placeholder={
                                isActivityModalityListLoading
                                    ? 'Loading modalities'
                                    : 'Select a modality'
                            }
                            options={activityModalityOptions}
                            selectedOption={
                                activityModalityLookup[
                                    formValues.activity_modality
                                ]
                            }
                            onChange={(e) =>
                                handleFieldEvent({
                                    activity_modality:
                                        e.detail.selectedOption.label,
                                })
                            }
                            loadingText="Loading modalities"
                            errorText="An error occurred while loading modalities"
                            empty="No modalities found"
                            statusType={getStatusType(
                                isActivityModalityListLoading,
                                isActivityModalityListLoaded,
                                activityModalityListError,
                            )}
                            disabled={!isActivityModalityListLoaded}
                            data-testid={`EditBaseDetailsActivityModality`}
                        />
                    </FormField>

                    {isLearningGoalsEnabled &&
                        (() => {
                            return (
                                hasOrganizationId(formValues.customers) && (
                                    <OrganizationGoalsSelect
                                        customers={formValues.customers}
                                        selectedGoalIds={
                                            formValues.learning_goal_ids ?? []
                                        }
                                        onChange={(goalIds) =>
                                            handleFieldEvent({
                                                learning_goal_ids: goalIds,
                                            })
                                        }
                                    />
                                )
                            );
                        })()}
                </SpaceBetween>
            </Container>
        </>
    );
};

export default EditBaseDetailsFormSection;
